import { useWebsiteSettings } from 'client/store'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import { useMutationWithValidation } from 'src/editor/api/hooks/use-api-mutation-with-validation'
import { websitePagesQuery } from 'src/editor/api/queries/website-pages-query'
import { getWebsiteId } from 'src/editor/store/website-settings/website-settings-selector'
import { ConfirmationDialogWindow } from 'src/editor/shared/ui-lib/modal-window/dialog-window/confirmation-dialog-window'
import { localeNames } from 'common/constants/localeTypes'
import {
  addWebsitePageLocale,
  copyPageLocale,
} from 'src/editor/api/website-pages-api'

interface AddPageLocaleProps {
  websitePageSetId: number
  localeToAdd: PageLocaleEnum | null
  onFinallyAddLocale: () => void
}

export const AddPageLocale = ({
  websitePageSetId,
  localeToAdd,
  onFinallyAddLocale,
}: AddPageLocaleProps) => {
  const { t } = useTranslation()
  const [copyPagesLocale, setCopyPagesLocale] = useState<PageLocaleEnum | null>(
    null,
  )

  const websiteId = useWebsiteSettings(getWebsiteId) as number

  const handleCloseCopyPages = () => {
    setCopyPagesLocale(null)
  }

  const { fetcher: fetcherAddLocale } = useMutationWithValidation({
    requestFunction: addWebsitePageLocale,
    successToastMessage: t(
      'page_settings.locales.add_locale.added_successfully',
    ),
    handleSuccess: () => {
      setCopyPagesLocale(localeToAdd)
    },
    handleFinally: () => {
      onFinallyAddLocale()
    },
    invalidateKeyOnSuccess: websitePagesQuery.getQueryKey(websiteId),
  })

  const { fetcher: fetcherCopyPages, isLoading: isLoadingCopyPages } =
    useMutationWithValidation({
      requestFunction: copyPageLocale,
      successToastMessage: t(
        'page_settings.locales.copy_pages.copied_successfully',
      ),
      handleSuccess: () => {
        setCopyPagesLocale(null)
      },
      invalidateKeyOnSuccess: websitePagesQuery.getQueryKey(websiteId),
    })

  useEffect(() => {
    if (localeToAdd) {
      fetcherAddLocale({
        websitePageSetId,
        locale: localeToAdd as PageLocaleEnum,
      })
    }
  }, [localeToAdd])

  const handleCopyPages = () => {
    fetcherCopyPages({
      websitePageSetId,
      locale: copyPagesLocale as PageLocaleEnum,
    })
  }

  return (
    <ConfirmationDialogWindow
      isOpened={!!copyPagesLocale}
      onClose={handleCloseCopyPages}
      title={t(
        'page_settings.locales.copy_pages.confirmation_window.title_icu',
        {
          locale: copyPagesLocale ? t(localeNames[copyPagesLocale]) : '',
        },
      )}
      description={t(
        'page_settings.locales.copy_pages.confirmation_window.description',
      )}
      cancelButtonProps={{
        children: t('global.skip'),
        onClick: handleCloseCopyPages,
      }}
      confirmButtonProps={{
        isLoading: isLoadingCopyPages,
        onClick: handleCopyPages,
        children: t(
          'page_settings.locales.copy_pages.confirmation_window.copy_button',
        ),
      }}
    />
  )
}
